import { CustomerAddress, Recipient, OrderStatus, DeliveryTime, DeliveryTimeType } from '@models';
import { createSlice } from '@reduxjs/toolkit';

export interface OrderProduct {
  name: string;
  productId: number;
  quantity: number;
  unitPrice: number;
  discountPercentage?: number;
}

export interface PaymentMethodType {
  approvalUrl?: string;
  country: string;
  currency?: string;
  expirationDate: string;
  id: string;
  label: string;
  merchantAccountId: string;
  method: string;
  scheme: string;
  type: string;
}

export interface OrderState {
  deliveryInstructions?: string;
  orderDiscountPercentage?: number;
  products: OrderProduct[];
  recipient: Recipient;
  shippingAddress?: CustomerAddress;
  totalPrice: number;
  noChargePrice?: number;
  selectedPaymentMethod?: PaymentMethodType;
  orderStatus: OrderStatus[];
  deliveryTime: DeliveryTime;
}

const updateTotalPrice = (state: { value: OrderState }) =>
  (state.value.totalPrice = state.value.products?.reduce((total, product) => {
    const price = product.unitPrice || 0;

    if (product.discountPercentage) {
      return total + price * product.quantity * (1 - product.discountPercentage);
    }

    return total + price * product.quantity;
  }, 0));

export const orderSlice = createSlice({
  name: 'order',
  initialState: {
    value: {
      products: [],
      recipient: { type: 'OnlyCustomer' },
      totalPrice: 0,
      selectedPaymentMethod: undefined,
      orderStatus: JSON.parse(localStorage.getItem('orderStatus') || '[]'),
      deliveryTime: { type: 'ASAP' },
    } as OrderState,
  },
  reducers: {
    addProductToOrder: (state, action: { type: string; payload: OrderProduct }) => {
      const existingIndex = state.value.products.findIndex((product) => product.productId === action.payload.productId);

      if (existingIndex >= 0) {
        state.value.products[existingIndex].quantity += action.payload.quantity;
        state.value.products[existingIndex].unitPrice = action.payload.unitPrice;
      } else {
        state.value.products.push(action.payload);
      }

      updateTotalPrice(state);
    },

    updateProduct: (state, action: { type: string; payload: Partial<OrderProduct> }) => {
      const index = state.value.products.findIndex((product) => product.productId === action.payload.productId);

      if (index === -1) return;

      // If quantity is 0, remove the product
      if (action.payload.quantity === 0) {
        state.value.products.splice(index, 1);
      } else {
        state.value.products[index] = { ...state.value.products[index], ...action.payload };
      }

      updateTotalPrice(state);
    },

    setDeliveryInstructions: (state, action: { type: string; payload: string | undefined }) => {
      state.value.deliveryInstructions = action.payload;
    },

    setRecipient: (state, action: { type: string; payload: Recipient }) => {
      state.value.recipient = action.payload;
    },

    setShippingAddress: (state, action: { type: string; payload: CustomerAddress | undefined }) => {
      // Reset to initial state because it shouldn't persist when the address changes.
      state.value = {
        ...state.value,
        products: [],
        recipient: { type: 'OnlyCustomer' },
        shippingAddress: action.payload,
        totalPrice: 0,
      };
    },

    setOrderDiscount: (state, action: { type: string; payload: number }) => {
      state.value.orderDiscountPercentage = action.payload;
    },

    setTotalPrice: (state) => {
      state.value.totalPrice = state.value.products?.reduce((total, product) => {
        const price = product.unitPrice || 0;

        if (product.discountPercentage) {
          return total + price * product.quantity * (1 - product.discountPercentage);
        }

        return total + price * product.quantity;
      }, 0);
    },
    setPaymentMethod: (state, action) => {
      state.value.selectedPaymentMethod = action.payload;
    },
    setOrderStatus: (state, action) => {
      state.value.orderStatus = action.payload;
      localStorage.setItem('orderStatus', JSON.stringify(action.payload));
    },
    resetOrder: (state) => {
      state.value = {
        products: [],
        recipient: { type: 'OnlyCustomer' },
        totalPrice: 0,
        selectedPaymentMethod: undefined,
        orderStatus: [],
        deliveryInstructions: undefined,
        deliveryTime: { type: DeliveryTimeType.ASAP },
      };
    },
    setDeliveryTime: (state, action: { payload: DeliveryTime }) => {
      state.value.deliveryTime = action.payload;
    },
  },
});

export const {
  addProductToOrder,
  setDeliveryInstructions,
  setRecipient,
  setShippingAddress,
  setOrderDiscount,
  setOrderStatus,
  setTotalPrice,
  updateProduct,
  setPaymentMethod,
  resetOrder,
  setDeliveryTime,
} = orderSlice.actions;

export default orderSlice.reducer;
